/**
 * @file new-request.tsx
 * @module app/modules/advertisement/tabs/new-request
 * @description Renders the new request advertisement tab, presenting campaign records for its corresponding lifecycle state.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useState } from 'react';
import { Button } from 'react-bootstrap';
import DeleteModal from '../../../modals/delete';
import APICallService from '../../../../api/apiCallService';
import { advertisements } from '../../../../api/apiEndPoints';
import { success } from '../../../../Global/toast';
import AdvertisementDetails from '../../../modals/advertisement-details';
import Loader from '../../../../Global/loader';
import { AdvertisementRequest, Advertising, HomePage, View } from '../../../../utils/constants';
import Method from '../../../../utils/methods';
import Pagination from '../../../../Global/pagination';
import { useAuth } from '../../auth';
import { getKey } from '../../../../Global/history';
import ThreeDotMenu from '../../../../_admin/assets/media/svg_uTrade/three-dot.svg';
import { CustomSelectTable } from '../../../custom/Select/CustomSelectTable';
function NewRequest(props: any) {
  const { currentUser } = useAuth();
  const [showAdDetails, setShowAdDetails] = useState(false);
  const [advertisement, setAdvertisement] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [error, setError] = useState('');
  const [menuShow, setMenuShow] = useState(false);
  const [menuId, setMenuId] = useState<number>(-1);

  const openMenuOnClick = async (id: any) => {
    setMenuId(id);
    setMenuShow(true);
  };
  const onMenuClose = async () => {
    setMenuId(-1);
    setMenuShow(false);
  };
  const onMenuOpen = async (id: any) => {
    setMenuId(id);
    setMenuShow(true);
  };

  const handleOption = async (event: any, index: number, data: any) => {
    if (!event) return;
    if (event.value === 2 || event.value === 'delete') {
      setSelectedAd(data._id || data);
      setShowDeleteModal(true);
    }
  };

  const handleDelete = async () => {
    setDeleteLoader(true);
    let apiService = new APICallService(
      advertisements.deleteAdvertisement,
      selectedAd,
      "",
      "",
      "",
      true,
      Advertising
    );
    let response = await apiService.callAPI();
    if (response && response.error) {
      setError(response.error);
    } else {
      success('Advertisement deleted successfully');
      setShowDeleteModal(false);
      // refresh current tab
      props.handleCurrentPage && props.handleCurrentPage(props.currentPage, AdvertisementRequest);
    }
    setDeleteLoader(false);
  };
  return (
    <>
      {showAdDetails ? (
        <AdvertisementDetails
          show={showAdDetails}
          advertisement={advertisement}
          onHide={() => setShowAdDetails(false)}
        />
      ) : (
        <></>
      )}
      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedAd(null);
            setError('');
          }}
          handleDelete={handleDelete}
          error={error}
          loading={deleteLoader}
          serviceName="advertisement"
          message={`Are you sure you want to delete this advertisement?`}
          warnMessage={`By continuing, the advertisement will be removed permanently`}
        />
      )}
      <div className="table-responsive">
        <table className="table table-rounded table-row-bordered align-middle gy-6 mb-0">
          <thead>
            <tr className="fs-16 fw-600">
              <th className="min-w-175px">Vendor name</th>
              <th className="min-w-150px">Ad placement</th>
              <th className="min-w-125px">IsPharmacy</th>
              <th className="min-w-200px">Start & End date</th>
              <th className="min-w-125px">Cost per day</th>
              <th className="min-w-80px text-end"></th>
            </tr>
          </thead>
          <tbody>
            {props.fetchLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="d-flex justify-content-center">
                    <Loader loading={props.fetchLoading} />
                  </div>
                </td>
              </tr>
            ) : props.advertisement && props.advertisement.length ? (
              props.advertisement.map((advertiseVal: any, index: number) => (
                <tr key={advertiseVal._id}>
                  <td>
                    <span className="fs-15 fw-500">{advertiseVal.business.ownerName}</span>
                  </td>
                  <td>
                    <span className="fs-15 fw-500">{advertiseVal.placement === HomePage ? 'Home page' : 'Category page'}</span>
                  </td>
                  <td>
                    <span className="fs-15 fw-500">{advertiseVal.isPharmacyAd ? 'Yes' : 'No'}</span>
                  </td>
                  <td>
                    <span className="fs-15 fw-500">
                      {Method.convertDateToDDMMYYYY(advertiseVal.startDate) + ' - ' + Method.convertDateToDDMMYYYY(advertiseVal.endDate)}
                    </span>
                  </td>
                  <td>
                    <span className="fs-15 fw-500">TSh {Method.getGeneralizedAmount(advertiseVal.amount)}</span>
                  </td>
                  <td className="text-end">
                    <div className='d-flex gap-4'>
                    {Method.hasPermission(Advertising, View, currentUser) ? (
                      <Button
                        onClick={() => {
                          setShowAdDetails(true);
                          setAdvertisement(advertiseVal);
                        }}
                        className="uw-btn uw-btn-primary uw-btn-sm btn-table"
                        data-analytics-event="advertisement_new_request_view_details_clicked"
                      >
                        View details
                      </Button>
                    ) : null}
                    <CustomSelectTable
                      marginLeft={'0px'}
                      width={'130px'}
                      placeholder={<img className="img-fluid" width={18} height={5} src={ThreeDotMenu} alt="" />}
                      backgroundColor="white"
                      options={[{ label: (<button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4" data-analytics-event="advertisement_new_request_delete_clicked">Delete</button>), value: 2 }]}
                      show={menuShow && index === menuId}
                      onMenuClose={onMenuClose}
                      openMenuOnClick={() => openMenuOnClick(index)}
                      onMenuOpen={() => onMenuOpen(index)}
                      onChange={(event: any) => handleOption(event, index, advertiseVal)}
                    />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <div className="d-flex justify-content-center">
                    <span className="fs-15 fw-500">No Data found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
     {!props.fetchLoading && props.advertisement && props.advertisement.length ? (
        <Pagination
          totalRecords={props.total}
          currentPage={props.currentPage}
          handleCurrentPage={(event: any) => { props.handleCurrentPage(event, AdvertisementRequest); }}
          handleNextPage={(event: any) => { props.handleNextPage(event, AdvertisementRequest); }}
          handlePreviousPage={(event: any) => { props.handlePreviousPage(event, AdvertisementRequest); }}
          pageLimit={parseInt(props.pageLimit)}
          handlePageLimit={(event: any) => { props.handlePageLimit(event, AdvertisementRequest); }}
        />
      ) :  <></>}
    </>
  );
}
export default NewRequest;
